import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import {
  Lightning,
  CircleNotch,
  Warning,
  ArrowClockwise,
  Eye,
  Cpu,
  Database,
  ChartBar,
  Pulse,
  ShieldCheck,
  ShieldWarning,
  CaretDown,
} from "@phosphor-icons/react";
import { useSelector } from "react-redux";
import api from "../../../services/authService";
import MatterProcessing from "../../MatterProcessing/MatterProcessing";

// This page polls on a timer, always. It used to poll ONLY when the socket
// was down and otherwise wait to be nudged; when a nudge did not arrive the
// page froze on an old snapshot with nothing on screen saying so. On
// 2026-08-13 Timothy Williams' file finished at 14:58 and the dashboard was
// still showing 102 extracting / 69 categorizing / 87 graphing at 16:00.
//
// The 2026-07-31 database incident this replaced was NOT caused by polling
// itself: every request recomputed the metrics, so 58 slow copies stacked.
// The server now computes the snapshot on a 30s beat and serves it from
// cache, and `inFlightRef` drops a poll that arrives while one is open, so a
// poll is a cheap cache read that cannot stack.
const POLL_INTERVAL = 10_000;
// Longer than a cold snapshot computation, short enough that a wedged
// request surfaces as an error the user can retry instead of hanging.
const REQUEST_TIMEOUT = 45_000;

const STAGE_LABELS = {
  extraction: "Extraction",
  categorization: "Categorization",
  graph: "Graph",
  embedding: "Embedding",
};

const MATTER_STAGE_COLUMNS = [
  ["extraction", "Extracting"],
  ["categorization", "Categorizing"],
  ["graph", "Graphing"],
  ["embedding", "Embedding"],
];

const STATUS_COLORS = {
  Completed: "#10B981",
  Extracted: "#0C53BB",
  Categorized: "#8B5CF6",
  Graph_Built: "#F59E0B",
  Indexed: "#06B6D4",
  Needs_Review: "#EF4444",
  Error: "#EF4444",
  failed: "#EF4444",
  Queued: "#6B7280",
  Processing: "#0C53BB",
  Pending: "#7C3AED",
};

const AIDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warming, setWarming] = useState(false);
  const [retrying, setRetrying] = useState({});
  const [expandedMatterId, setExpandedMatterId] = useState(null);
  const inFlightRef = useRef(false);

  const snapshotGeneration = useSelector(
    (state) => state.websocket?.pipelineDashboardGeneration
  );

  const fetchDashboard = useCallback(async () => {
    // Never queue a request behind a slow one — that stacking is what
    // saturated the database, and a nudge can arrive mid-flight.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await api.get("/pipeline/dashboard/", {
        timeout: REQUEST_TIMEOUT,
      });
      setData(res.data);
      setError(null);
      setWarming(false);
    } catch (err) {
      if (err.response?.data?.reason === "snapshot_warming") {
        // First snapshot is still being computed. It will nudge us when it
        // lands, so wait rather than asking again and competing with it.
        setWarming(true);
        setError(null);
      } else {
        setError(err.response?.data?.error || "Failed to load dashboard");
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchDashboard();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [fetchDashboard]);

  // The server published a snapshot whose numbers differ from the last one.
  useEffect(() => {
    if (!snapshotGeneration) return;
    fetchDashboard();
  }, [snapshotGeneration, fetchDashboard]);

  // Always on, socket or not: the nudge is an optimization for instant
  // updates, never the only way fresh numbers arrive. Hidden tabs skip the
  // fetch (the visibilitychange handler above refetches on return) so a
  // backgrounded dashboard costs nothing.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      fetchDashboard();
    }, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchDashboard]);

  const handleRetry = async (docId) => {
    setRetrying((prev) => ({ ...prev, [docId]: true }));
    try {
      await api.post(`/pipeline/retry/${docId}/`);
      await fetchDashboard();
    } catch {
      /* retry failure is visible in dashboard on next poll */
    } finally {
      setRetrying((prev) => ({ ...prev, [docId]: false }));
    }
  };

  if (loading && !data) {
    return (
      <DashboardWrap>
        <LoadingState>
          <CircleNotch size={32} className="spin" />
          <span>Loading pipeline data...</span>
        </LoadingState>
      </DashboardWrap>
    );
  }

  if (warming && !data) {
    return (
      <DashboardWrap>
        <LoadingState>
          <CircleNotch size={32} className="spin" />
          <span>Gathering pipeline data — this updates automatically.</span>
        </LoadingState>
      </DashboardWrap>
    );
  }

  if (error && !data) {
    return (
      <DashboardWrap>
        <ErrorState>
          <Warning size={28} color="#EF4444" />
          <span>{error}</span>
          <RetryBtn onClick={fetchDashboard}>Retry</RetryBtn>
        </ErrorState>
      </DashboardWrap>
    );
  }

  if (!data) return null;

  const counts = deriveDashboardCounts(data);
  const { inFlight, completed, needsReview, zombieCount, totalProcessed } = counts;

  const matters = sortMattersForBoard(
    Array.isArray(data.matters) ? data.matters : []
  );
  const redCount = matters.filter((m) => rowHealth(m) === "red").length;
  const yellowCount = matters.filter((m) => rowHealth(m) === "yellow").length;

  const renderMatterRow = (m) => {
    const matterId = String(m.id);
    const isExpanded = expandedMatterId === matterId;
    const health = rowHealth(m);
    const stageCounts = m.stage_counts || {};
    const processingStatus =
      m.processing_status !== undefined
        ? m.processing_status
        : m.documents_in_flight > 0
          ? "processing"
          : null;
    const processingStatusLabel =
      processingStatus === "processing"
        ? "Processing"
        : processingStatus === "pending"
          ? "Pending"
          : "—";
    const clientLabel =
      m.client_name || m.display_name || `Matter ${m.id}`;
    const matterLabel =
      m.display_name && m.display_name !== clientLabel
        ? m.display_name
        : "";
    return (
      <Fragment key={m.id}>
        <MatterRow
          data-testid={`matter-row-${m.id}`}
          data-health={health || "ok"}
          $expanded={isExpanded}
          $health={health}
        >
          <td>
            <ClientCell>
              <MatterExpandButton
                type="button"
                $expanded={isExpanded}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Hide" : "Show"} processing details for ${clientLabel}`}
                onClick={() =>
                  setExpandedMatterId((current) =>
                    current === matterId ? null : matterId
                  )
                }
              >
                <CaretDown size={16} weight="bold" />
              </MatterExpandButton>
              <ClientIdentity>
                <ClientName title={clientLabel}>{clientLabel}</ClientName>
                {matterLabel && (
                  <MatterLabel title={matterLabel}>{matterLabel}</MatterLabel>
                )}
              </ClientIdentity>
            </ClientCell>
          </td>
          <td data-testid={`matter-status-${m.id}`}>
            <StatusBadge $status={processingStatusLabel}>
              {processingStatusLabel}
            </StatusBadge>
          </td>
          {MATTER_STAGE_COLUMNS.map(([key]) => (
            <td key={key}>
              <MatterCount $active={stageCounts[key] > 0}>
                {stageCounts[key] == null
                  ? "—"
                  : formatNumber(stageCounts[key])}
              </MatterCount>
            </td>
          ))}
          <td><MatterCount>{formatNumber(m.documents_completed || 0)}</MatterCount></td>
          <td>{formatNumber(m.documents_total || 0)}</td>
          <td className="amiss" data-testid={`matter-amiss-${m.id}`}>
            <AmissCell $health={health}>
              <span>{describeAmiss(m)}</span>
              {health === "red" && (
                <DecideBtn
                  type="button"
                  data-testid={`matter-decide-${m.id}`}
                  onClick={() => setExpandedMatterId(matterId)}
                >
                  Decide
                </DecideBtn>
              )}
            </AmissCell>
          </td>
          <td className="updated">{formatAgo(m.last_activity_at)}</td>
        </MatterRow>
        {isExpanded && (
          <MatterDetailRow data-testid={`matter-details-${m.id}`}>
            <td colSpan={10}>
              <MatterProcessing matterId={m.id} embedded />
            </td>
          </MatterDetailRow>
        )}
      </Fragment>
    );
  };

  return (
    <DashboardWrap>
      <TopBar>
        <TopBarLeft>
          <Pulse size={20} weight="bold" />
          <span>Pipeline Status</span>
          <PipelineBadge $active={data.pipeline_enabled}>
            {data.pipeline_enabled ? "Enabled" : "Disabled"}
          </PipelineBadge>
        </TopBarLeft>
        <RefreshBtn onClick={fetchDashboard}>
          <ArrowClockwise size={16} />
          Refresh
        </RefreshBtn>
      </TopBar>

      {zombieCount > 0 && (
        <ZombieBanner data-testid="zombie-banner">
          <Warning size={18} color="#92400E" weight="fill" />
          <ZombieText>
            <strong>{formatNumber(zombieCount)} stale documents</strong> have a
            non-terminal <code>pipeline_status</code> but no live stage
            attempts. They are NOT actually being processed — leftovers from
            stale-lease reclaim or pre-PR-#361 crash recovery. They do not
            count toward &quot;In Flight&quot;. Investigate before bulk-cleaning.
          </ZombieText>
        </ZombieBanner>
      )}

      {/* One view of every claim file in the pipeline. A row is red when the
          file is held on a document a person must decide about, yellow when
          something is amiss but the system is handling it (retrying a
          processing error, or nothing has moved for a while with nothing
          failed), and plain when it is moving or finished. This replaced a
          three-bucket layout (Needs Attention / Processing / Completed) in
          which a claim file with one broken document vanished from
          "Processing" while its other 300 documents were still being worked,
          and "Needs Attention" was 80% unfiled mail and 18% errors the
          system should simply have retried. */}
      {matters.length > 0 && (
        <Section data-testid="matter-rollup-section">
          <SectionTitle>
            <Cpu size={18} /> Client Processing ({matters.length})
            <HealthLegend>
              <HealthChip $health="red" data-testid="health-chip-red">
                {redCount} {redCount === 1 ? "needs" : "need"} your decision
              </HealthChip>
              <HealthChip $health="yellow" data-testid="health-chip-yellow">
                {yellowCount} being retried or stalled
              </HealthChip>
            </HealthLegend>
          </SectionTitle>
          <MatterTable>
            <thead>
              <tr>
                <th>Client</th>
                <th>Status</th>
                {MATTER_STAGE_COLUMNS.map(([, label]) => <th key={label}>{label}</th>)}
                <th>Done</th>
                <th>Total</th>
                <th className="amiss">What&apos;s amiss</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>{matters.map(renderMatterRow)}</tbody>
          </MatterTable>
        </Section>
      )}

      <SectionRow>
        {/* Active stage breakdown */}
        <Section style={{ flex: 1 }}>
          <SectionTitle>
            <Database size={18} /> Stage Activity
          </SectionTitle>
          <StageTable>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Queued</th>
                <th>Leased</th>
                <th>Awaiting</th>
                <th>Total Succeeded</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(STAGE_LABELS).map((stage) => {
                const active = data.active_stages?.[stage] || {};
                const tokens = data.token_usage?.[stage] || {};
                return (
                  <tr key={stage}>
                    <td><StagePill $stage={stage}>{STAGE_LABELS[stage]}</StagePill></td>
                    <td>{active.queued || 0}</td>
                    <td>{active.leased || 0}</td>
                    <td>{active.awaiting_result || 0}</td>
                    <td>{tokens.attempts || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </StageTable>
        </Section>

        {/* Circuit breakers */}
        <Section style={{ flex: 0, minWidth: 260 }}>
          <SectionTitle>
            <ShieldCheck size={18} /> Circuit Breakers
          </SectionTitle>
          <CircuitList>
            {Object.keys(STAGE_LABELS).map((stage) => {
              const state = data.circuit_breakers?.[stage];
              const isOpen = state === "open";
              return (
                <CircuitRow key={stage} $open={isOpen}>
                  {isOpen ? (
                    <ShieldWarning size={18} color="#EF4444" weight="fill" />
                  ) : (
                    <ShieldCheck size={18} color="#10B981" weight="fill" />
                  )}
                  <span>{STAGE_LABELS[stage]}</span>
                  <CircuitBadge $open={isOpen}>
                    {isOpen ? "Open" : "Closed"}
                  </CircuitBadge>
                </CircuitRow>
              );
            })}
          </CircuitList>
        </Section>
      </SectionRow>

      {/* Token usage */}
      <Section>
        <SectionTitle>
          <ChartBar size={18} /> Token Usage
        </SectionTitle>
        <TokenGrid>
          {Object.keys(STAGE_LABELS).map((stage) => {
            const all = data.token_usage?.[stage] || {};
            const h24 = data.token_usage_24h?.[stage] || {};
            return (
              <TokenCard key={stage}>
                <TokenCardHeader>
                  <StagePill $stage={stage}>{STAGE_LABELS[stage]}</StagePill>
                </TokenCardHeader>
                <TokenRow>
                  <TokenLabel>Input</TokenLabel>
                  <TokenVal>{formatNumber(all.input_tokens || 0)}</TokenVal>
                </TokenRow>
                <TokenRow>
                  <TokenLabel>Output</TokenLabel>
                  <TokenVal>{formatNumber(all.output_tokens || 0)}</TokenVal>
                </TokenRow>
                <TokenDivider />
                <TokenRow>
                  <TokenLabel>24h Input</TokenLabel>
                  <TokenVal>{formatNumber(h24.input_tokens || 0)}</TokenVal>
                </TokenRow>
                <TokenRow>
                  <TokenLabel>24h Output</TokenLabel>
                  <TokenVal>{formatNumber(h24.output_tokens || 0)}</TokenVal>
                </TokenRow>
              </TokenCard>
            );
          })}
        </TokenGrid>
      </Section>

      {/* Active batches */}
      {Object.keys(data.active_batches || {}).length > 0 && (
        <Section>
          <SectionTitle>
            <Cpu size={18} /> Active Batches
          </SectionTitle>
          <StageTable>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Submitting</th>
                <th>Submitted</th>
                <th>Running</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.active_batches).map(([stage, states]) => (
                <tr key={stage}>
                  <td><StagePill $stage={stage}>{STAGE_LABELS[stage] || stage}</StagePill></td>
                  <td>{states.submitting || 0}</td>
                  <td>{states.submitted || 0}</td>
                  <td>{states.running || 0}</td>
                </tr>
              ))}
            </tbody>
          </StageTable>
        </Section>
      )}

      {/* DLQ */}
      {Object.keys(data.dlq_counts || {}).length > 0 && (
        <Section>
          <SectionTitle>
            <Warning size={18} color="#EF4444" /> Dead Letter Queue
          </SectionTitle>
          <StageTable>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Pending</th>
                <th>Retrying</th>
                <th>Resolved</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.dlq_counts).map(([stage, counts]) => (
                <tr key={stage}>
                  <td><StagePill $stage={stage}>{STAGE_LABELS[stage] || stage}</StagePill></td>
                  <td>{counts.pending || 0}</td>
                  <td>{counts.retrying || 0}</td>
                  <td>{counts.resolved || 0}</td>
                </tr>
              ))}
            </tbody>
          </StageTable>
        </Section>
      )}

      {/* Active documents */}
      {data.active_documents?.length > 0 && (
        <Section>
          <SectionTitle>
            <CircleNotch size={18} /> Active Documents ({data.active_documents.length})
          </SectionTitle>
          <DocTable>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Stage</th>
                <th>Category</th>
                <th>Admitted</th>
              </tr>
            </thead>
            <tbody>
              {data.active_documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="mono">{doc.id}</td>
                  <td className="name">{doc.name || "—"}</td>
                  <td>
                    <StatusBadge $status={doc.pipeline_status}>
                      {doc.pipeline_status}
                    </StatusBadge>
                  </td>
                  <td className="stage">{doc.current_stage || "—"}</td>
                  <td>{doc.category || "—"}</td>
                  <td className="mono">{formatAgo(doc.pipeline_admitted_at)}</td>
                </tr>
              ))}
            </tbody>
          </DocTable>
        </Section>
      )}

      {/* Review queue */}
      {data.review_documents?.length > 0 && (
        <Section>
          <SectionTitle>
            <Eye size={18} color="#EF4444" /> Review Queue ({data.review_documents.length})
          </SectionTitle>
          <DocTable>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Failed Stage</th>
                <th>Error</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.review_documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="mono">{doc.id}</td>
                  <td className="name">{doc.name || "—"}</td>
                  <td>
                    <StatusBadge $status={doc.pipeline_status}>
                      {doc.pipeline_status}
                    </StatusBadge>
                  </td>
                  <td>
                    <StagePill $stage={doc.failed_stage}>
                      {STAGE_LABELS[doc.failed_stage] || doc.failed_stage || "—"}
                    </StagePill>
                  </td>
                  <td className="error">
                    {doc.error_message
                      ? doc.error_message.slice(0, 120)
                      : doc.error_code || "—"}
                  </td>
                  <td>
                    <RetrySmallBtn
                      onClick={() => handleRetry(doc.id)}
                      disabled={retrying[doc.id]}
                    >
                      {retrying[doc.id] ? (
                        <CircleNotch size={14} className="spin" />
                      ) : (
                        <ArrowClockwise size={14} />
                      )}
                      Retry
                    </RetrySmallBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </DocTable>
        </Section>
      )}

      {totalProcessed === 0 &&
        inFlight === 0 &&
        completed === 0 &&
        needsReview === 0 &&
        zombieCount === 0 && (
          <EmptyState>
            <Lightning size={48} color="#CBD5E1" />
            <p>No documents have been processed yet.</p>
            <p style={{ fontSize: 13, color: "#94A3B8" }}>
              Use the AI Upload button in any Matter to send PDFs through the pipeline.
            </p>
          </EmptyState>
        )}
    </DashboardWrap>
  );
};

export default AIDashboard;

/* Helpers */

/**
 * Derive the four summary counts the dashboard renders.
 *
 * The backend (post fix/dashboard-source-of-truth) returns
 * `in_flight_count` / `completed_count` / `needs_review_count` /
 * `zombie_count` at the top level — read from
 * `pipeline_v3_stage_attempts`, the authoritative source. We use those
 * directly when present.
 *
 * Older backend builds return only `status_counts` (per-status counts on
 * `documents_document.pipeline_status`). Fall back to summing those so
 * the FE still renders during a deploy gap; the legacy formula
 * over-reports "in flight" because it counts zombies (docs with stale
 * pipeline_status but no live stage_attempt), which is the original bug
 * this whole change fixes.
 */
export function deriveDashboardCounts(data) {
  const sc = data.status_counts || {};
  const totalProcessed = Object.values(sc).reduce((a, b) => a + b, 0);

  const hasNewShape =
    typeof data.in_flight_count === "number" &&
    typeof data.completed_count === "number" &&
    typeof data.needs_review_count === "number";

  if (hasNewShape) {
    return {
      inFlight: data.in_flight_count,
      completed: data.completed_count,
      needsReview: data.needs_review_count,
      zombieCount:
        typeof data.zombie_count === "number" ? data.zombie_count : 0,
      totalProcessed,
    };
  }

  const completed = sc.Completed || 0;
  const needsReview =
    (sc.Needs_Review || 0) + (sc.Error || 0) + (sc.failed || 0);
  return {
    inFlight: Math.max(totalProcessed - completed - needsReview, 0),
    completed,
    needsReview,
    zombieCount: 0,
    totalProcessed,
  };
}

/** Older backends' only "something is wrong" signal. */
export function matterHasProblem(m) {
  return (m.documents_needs_review || 0) > 0 || (m.documents_zombie || 0) > 0;
}

export function matterIsProcessing(m) {
  if (m.processing_status === "processing" || m.processing_status === "pending") {
    return true;
  }
  if (m.processing_status === undefined) {
    // Older backend builds before `processing_status` shipped.
    return (m.documents_in_flight || 0) > 0;
  }
  return false;
}

/**
 * Row colour for the one-view board. Mirrors the server's `health`:
 *
 *   red    — held on a document a person must decide about
 *   yellow — amiss but the system's job: retrying a processing error, or
 *            nothing has moved for a while with nothing failed
 *   null   — moving or finished
 *
 * Older backends have no `health`; there a non-zero review/zombie count is
 * the best signal available and is shown yellow (we cannot tell whether it
 * is a decision or a retry).
 */
export function rowHealth(m) {
  if (m.health === "decision") return "red";
  if (m.health === "retrying" || m.health === "silent") return "yellow";
  if (m.health === undefined && matterHasProblem(m)) return "yellow";
  return null;
}

// Every document on a claim file waits on the client's list of claimed
// conditions being built first. When that step stalls, nothing has
// "failed", so without this the row would read as merely quiet.
const CONDITION_LIST_NOTE =
  "Waiting on the client's condition list; the system re-drives it every few hours";

export function describeAmiss(m) {
  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
  switch (m.health) {
    case "decision": {
      const n = m.documents_needs_decision || 0;
      const causes = (m.decision_causes || [])
        .map((c) => (c.count > 1 ? `${c.title} ×${c.count}` : c.title))
        .join("; ");
      const base = `Your decision: ${plural(n, "document")}${causes ? ` — ${causes}` : ""}`;
      return m.waiting_on_condition_list ? `${base}. ${CONDITION_LIST_NOTE}` : base;
    }
    case "retrying":
      if (m.waiting_on_condition_list) return CONDITION_LIST_NOTE;
      return `System retrying ${plural(m.documents_retrying || 0, "document")}`;
    case "silent": {
      if (m.waiting_on_condition_list) return CONDITION_LIST_NOTE;
      const mins = m.idle_minutes || 0;
      const ago = mins >= 60 ? `${Math.floor(mins / 60)}h` : `${mins}m`;
      return `Nothing has moved for ${ago}; nothing failed`;
    }
    case undefined: {
      if (!matterHasProblem(m)) return "—";
      const parts = [];
      if (m.documents_needs_review > 0)
        parts.push(
          `${plural(m.documents_needs_review, "document")} need${
            m.documents_needs_review === 1 ? "s" : ""
          } review`
        );
      if (m.documents_zombie > 0)
        parts.push(`${plural(m.documents_zombie, "stale document")}`);
      return parts.join("; ");
    }
    default:
      return "—";
  }
}

const HEALTH_RANK = { red: 0, yellow: 1 };

/** Red rows first, then yellow, then moving, then finished. Stable within. */
export function sortMattersForBoard(matters) {
  const rank = (m) => {
    const h = rowHealth(m);
    if (h) return HEALTH_RANK[h];
    return matterIsProcessing(m) ? 2 : 3;
  };
  return matters
    .map((m, i) => [m, i])
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1])
    .map(([m]) => m);
}

function formatNumber(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

/* Styled components */

const DashboardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  padding-bottom: 40px;
  font-family: Manrope, sans-serif;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .spin { animation: spin 1s linear infinite; }
`;

const LoadingState = styled.div`
  display: flex; align-items: center; gap: 12px;
  justify-content: center; padding: 80px 0; color: #64748B;
`;

const ErrorState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; padding: 80px 0; color: #64748B;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 60px 0; color: #94A3B8;
  p { margin: 0; }
`;

const TopBar = styled.div`
  display: flex; justify-content: space-between; align-items: center;
`;

const TopBarLeft = styled.div`
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; font-size: 16px; color: #1E293B;
`;

const ZombieBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: #FFFBEB; border: 1px solid #FCD34D;
  border-left: 4px solid #D97706;
  border-radius: 8px; padding: 12px 14px;
  font-size: 13px; color: #78350F; line-height: 1.5;
  code {
    background: rgba(180, 83, 9, 0.12); padding: 1px 5px;
    border-radius: 4px; font-size: 12px;
    font-family: "SF Mono", "Fira Code", monospace;
  }
`;

const ZombieText = styled.div`
  flex: 1;
  strong { color: #92400E; }
`;

const PipelineBadge = styled.span`
  font-size: 11px; font-weight: 600; padding: 3px 10px;
  border-radius: 99px;
  background: ${(p) => (p.$active ? "#DCFCE7" : "#FEE2E2")};
  color: ${(p) => (p.$active ? "#166534" : "#991B1B")};
`;

const RefreshBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  background: #F1F5F9; border: 1px solid #E2E8F0; border-radius: 6px;
  padding: 6px 14px; font-size: 13px; font-weight: 500; color: #475569;
  cursor: pointer; font-family: 'Manrope', sans-serif;
  &:hover { background: #E2E8F0; }
`;

const RetryBtn = styled.button`
  background: #0C53BB; color: #fff; border: none; border-radius: 6px;
  padding: 8px 20px; font-size: 13px; font-weight: 600; cursor: pointer;
  font-family: 'Manrope', sans-serif;
  &:hover { background: #0A47A3; }
`;

const RetrySmallBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  background: #F1F5F9; border: 1px solid #E2E8F0; border-radius: 5px;
  padding: 4px 10px; font-size: 12px; font-weight: 500; color: #475569;
  cursor: pointer; font-family: 'Manrope', sans-serif;
  &:hover { background: #E2E8F0; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SectionRow = styled.div`
  display: flex; gap: 16px;
  @media (max-width: 900px) { flex-direction: column; }
`;

const Section = styled.div`
  background: #fff; border: 1px solid #E2E8F0; border-radius: 10px;
  padding: 18px; display: flex; flex-direction: column; gap: 12px;
`;

const SectionTitle = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 600; color: #334155;
`;

const StageTable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: left; padding: 8px 10px; color: #64748B; font-weight: 500;
       border-bottom: 1px solid #F1F5F9; font-size: 12px; }
  td { padding: 8px 10px; border-bottom: 1px solid #F8FAFC; }
`;

const DocTable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: left; padding: 8px 10px; color: #64748B; font-weight: 500;
       border-bottom: 1px solid #F1F5F9; font-size: 12px;
       white-space: nowrap; }
  td { padding: 8px 10px; border-bottom: 1px solid #F8FAFC; }
  td.mono { font-family: "SF Mono", "Fira Code", monospace; font-size: 12px; color: #64748B; }
  td.name { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  td.stage { max-width: 200px; overflow: hidden; text-overflow: ellipsis;
             white-space: nowrap; font-size: 12px; color: #64748B; }
  td.error { max-width: 280px; overflow: hidden; text-overflow: ellipsis;
             white-space: nowrap; font-size: 12px; color: #94A3B8; }
`;

const STAGE_COLORS = {
  extraction: "#0C53BB",
  categorization: "#8B5CF6",
  graph: "#F59E0B",
  embedding: "#06B6D4",
};

const StagePill = styled.span`
  display: inline-block; padding: 2px 10px; border-radius: 99px;
  font-size: 12px; font-weight: 600;
  background: ${(p) => (STAGE_COLORS[p.$stage] || "#94A3B8") + "18"};
  color: ${(p) => STAGE_COLORS[p.$stage] || "#64748B"};
`;

const StatusBadge = styled.span`
  display: inline-block; padding: 2px 10px; border-radius: 99px;
  font-size: 11px; font-weight: 600;
  background: ${(p) => (STATUS_COLORS[p.$status] || "#94A3B8") + "18"};
  color: ${(p) => STATUS_COLORS[p.$status] || "#64748B"};
`;

const CircuitList = styled.div`
  display: flex; flex-direction: column; gap: 8px;
`;

const CircuitRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; border-radius: 8px;
  background: ${(p) => (p.$open ? "#FEF2F2" : "#F0FDF4")};
  font-size: 13px; font-weight: 500;
  span { flex: 1; }
`;

const CircuitBadge = styled.span`
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 99px;
  background: ${(p) => (p.$open ? "#FEE2E2" : "#DCFCE7")};
  color: ${(p) => (p.$open ? "#991B1B" : "#166534")};
`;

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const TokenCard = styled.div`
  border: 1px solid #F1F5F9; border-radius: 8px; padding: 14px;
  display: flex; flex-direction: column; gap: 6px;
`;

const TokenCardHeader = styled.div`
  margin-bottom: 4px;
`;

const TokenRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
`;

const TokenLabel = styled.span`
  font-size: 12px; color: #94A3B8;
`;

const TokenVal = styled.span`
  font-size: 13px; font-weight: 600; color: #334155;
  font-family: "SF Mono", "Fira Code", monospace;
`;

const TokenDivider = styled.hr`
  border: none; border-top: 1px solid #F1F5F9; margin: 4px 0;
`;

const MatterTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
  th {
    text-align: left;
    padding: 8px 10px;
    color: #64748B;
    font-weight: 600;
    border-bottom: 1px solid #E2E8F0;
    font-size: 11px;
    white-space: nowrap;
  }
  th:first-child { width: 24%; }
  th.amiss { width: 24%; text-align: left; }
  th:not(:first-child):not(.amiss) { text-align: center; }
  td {
    padding: 10px;
    border-bottom: 1px solid #F1F5F9;
    vertical-align: middle;
  }
  td:not(:first-child):not(.amiss) { text-align: center; }
  td.updated {
    color: #94A3B8;
    font-family: "SF Mono", "Fira Code", monospace;
    font-size: 11px;
    white-space: nowrap;
  }
`;

const ROW_TINTS = {
  red: { bg: "#FEF2F2", hover: "#FEE2E2", edge: "#DC2626", fg: "#B91C1C" },
  yellow: { bg: "#FFFBEB", hover: "#FEF3C7", edge: "#F59E0B", fg: "#92400E" },
};

const MatterRow = styled.tr`
  background: ${(p) =>
    p.$health ? ROW_TINTS[p.$health].bg : p.$expanded ? "#F8FAFC" : "#fff"};
  box-shadow: ${(p) =>
    p.$health ? `inset 4px 0 0 ${ROW_TINTS[p.$health].edge}` : "none"};
  &:hover {
    background: ${(p) => (p.$health ? ROW_TINTS[p.$health].hover : "#F8FAFC")};
  }
`;

const AmissCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  text-align: left;
  color: ${(p) => (p.$health ? ROW_TINTS[p.$health].fg : "#94A3B8")};
  font-weight: ${(p) => (p.$health === "red" ? 600 : 500)};
  font-size: 12px;
  line-height: 1.3;
`;

const DecideBtn = styled.button`
  flex: 0 0 auto;
  padding: 3px 10px;
  border-radius: 6px;
  border: 1px solid #DC2626;
  background: #DC2626;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  &:hover { background: #B91C1C; }
`;

const HealthLegend = styled.span`
  display: inline-flex;
  gap: 8px;
  margin-left: auto;
  font-weight: 500;
`;

const HealthChip = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 99px;
  background: ${(p) => ROW_TINTS[p.$health].bg};
  color: ${(p) => ROW_TINTS[p.$health].fg};
  border: 1px solid ${(p) => ROW_TINTS[p.$health].edge};
`;

const MatterDetailRow = styled.tr`
  background: #F8FAFC;
  > td {
    padding: 0 12px 14px 42px;
    text-align: left !important;
  }
`;

const ClientCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  text-align: left;
`;

const MatterExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border: 1px solid #CBD5E1;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  svg { transform: ${(p) => (p.$expanded ? "rotate(180deg)" : "none")}; }
  &:hover { border-color: #0C53BB; color: #0C53BB; }
`;

const ClientIdentity = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
`;

const ClientName = styled.span`
  color: #0F172A;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MatterLabel = styled.span`
  color: #64748B;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MatterCount = styled.span`
  color: ${(p) =>
    p.$alert
      ? "#DC2626"
      : p.$active
        ? "#0C53BB"
        : "#64748B"};
  font-weight: ${(p) => (p.$alert || p.$active ? 700 : 500)};
`;
